import { Resources, type TFunction } from "Translations/Resources";
import {
  ErrorCode,
  type FetchResponse,
  type PayTransactionResponse,
  type PostTransactionsPayFetchResponse,
} from "Infrastructure/Api/Api";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { toast } from "sonner";

const RESOURCES = Resources.TransactionErrors;
const LIMIT_ERRORS = Resources.TransactionErrors.TransactionLimits;

export type ErrorType = Exclude<
  PostTransactionsPayFetchResponse,
  FetchResponse<PayTransactionResponse, 200>
>; // all possible response types, except the 200

export const handleTransactionPayError = (
  error: ErrorType,
  t: TFunction,
  showToast = true,
): string | undefined => {
  const message = getTransactionErrorMessage(error, t);
  if (!message) {
    return;
  }
  const shouldShowSentryFeedbackDialog = error.status > 405 || !error.status;

  if (message === t(Resources.TransactionErrors.UnknownError)) {
    logError(new Error(message), error, shouldShowSentryFeedbackDialog);
  }

  if (showToast) {
    toast.error(message);
  }

  return message;
};

export const getTransactionErrorMessage = (
  { data, status }: ErrorType,
  t: TFunction,
) => {
  const errors = data?.errors || {};

  if (status === 404) {
    return t(RESOURCES.TransactionNotFound);
  }

  if (errors.pispAuthentication) {
    return;
  }

  //Limits errors
  if (errors.generatedTransactionLimitExceeded) {
    return t(LIMIT_ERRORS.generatedTransactionLimitExceeded);
  }
  if (errors.oneTimeSendLimitExceeded) {
    return t(LIMIT_ERRORS.oneTimeSendLimitExceeded);
  }
  if (errors.dailySendLimitExceeded) {
    return t(LIMIT_ERRORS.dailySendLimitExceeded);
  }
  if (errors.generatedDailySendLimitExceeded) {
    return t(LIMIT_ERRORS.generatedDailySendLimitExceeded);
  }
  if (errors.monthlySendLimitExceeded) {
    return t(LIMIT_ERRORS.monthlySendLimitExceeded);
  }
  if (errors.generatedMonthlySendLimitExceeded) {
    return t(LIMIT_ERRORS.generatedMonthlySendLimitExceeded);
  }
  if (errors.generatedTransactionLimitExceededIncompleteRegistration) {
    return t(
      LIMIT_ERRORS.generatedTransactionLimitExceededIncompleteRegistration,
    );
  }
  if (errors.oneTimeSendLimitExceededIncompleteRegistration) {
    return t(LIMIT_ERRORS.oneTimeSendLimitExceededIncompleteRegistration);
  }
  if (errors.dailySendLimitExceededIncompleteRegistration) {
    return t(LIMIT_ERRORS.dailySendLimitExceededIncompleteRegistration);
  }
  if (errors.generatedDailySendLimitExceededIncompleteRegistration) {
    return t(
      LIMIT_ERRORS.generatedDailySendLimitExceededIncompleteRegistration,
    );
  }
  if (errors.monthlySendLimitExceededIncompleteRegistration) {
    return t(LIMIT_ERRORS.monthlySendLimitExceededIncompleteRegistration);
  }
  if (errors.generatedMonthlySendLimitExceededIncompleteRegistration) {
    return t(
      LIMIT_ERRORS.generatedMonthlySendLimitExceededIncompleteRegistration,
    );
  }
  if (errors.oneTimeReceiveLimitExceededPreApproval) {
    return t(LIMIT_ERRORS.oneTimeReceiveLimitExceededPreApproval);
  }
  if (errors.cumulativeReceiveLimitExceededPreApproval) {
    return t(LIMIT_ERRORS.cumulativeReceiveLimitExceededPreApproval);
  }
  if (errors.currencyNotSupportedPreApproval) {
    return t(LIMIT_ERRORS.currencyNotSupportedPreApproval);
  }
  if (errors.unspecifiedError) {
    return t(LIMIT_ERRORS.unspecifiedError);
  }

  //Invalid P2P transaction of a moone lite user
  if (errors.User) {
    return t(RESOURCES.User);
  }

  //No Primary account set
  if (errors.BankAccount) {
    return t(RESOURCES.BankAccount);
  }

  //Invalid bank account for receiving payment
  if (errors.ReceiverBankAccountID) {
    return t(RESOURCES.InvalidBankAccountForReceivingPayment);
  }

  //Balance Errors
  if (errors.accountBalance) {
    return t(RESOURCES.InsufficientBalance);
  }
  if (errors.balanceStatus) {
    return t(RESOURCES.BalanceVerificationFailed);
  }

  //Payment Errors
  if (errors.accountConflict) {
    return t(RESOURCES.SameAccountError);
  }
  if (errors.accountInvalidCurrency) {
    return t(RESOURCES.InvalidAccountCurrency);
  }
  if (errors.accountInvalid) {
    return t(RESOURCES.AccountInvalid);
  }
  if (errors.transactionInvalidCurrency) {
    return t(RESOURCES.InvalidTransactionCurrency);
  }
  if (errors.transactionForbidden) {
    return t(RESOURCES.TransactionTypeNotAllowed);
  }
  if (errors.limitExceeded) {
    return t(RESOURCES.TransactionLimitExceeded);
  }
  if (errors.authLimitExceeded) {
    return t(RESOURCES.AuthLimitExceeded);
  }
  if (errors.invalidJsonFormat) {
    return t(RESOURCES.InvalidJsonFormat);
  }
  if (errors.generalRejection) {
    return t(RESOURCES.GeneralRejection);
  }
  if (errors.notUniqueTransactionReference) {
    return t(RESOURCES.NotUniqueTransactionReference);
  }
  if (errors.invalidCharacterSet) {
    return t(RESOURCES.InvalidCharacterSet);
  }
  if (errors.invalidAccountNumber) {
    return t(RESOURCES.InvalidAccountNumber);
  }
  if (errors.fieldInvalid) {
    return t(RESOURCES.FieldInvalid);
  }
  if (errors.fieldMissing) {
    return t(RESOURCES.FieldMissing);
  }
  if (errors.transactionAmountForbidden) {
    return t(RESOURCES.TransactionAmountForbidden);
  }
  if (errors.transactionDecimalNumbersForbidden) {
    return t(RESOURCES.TransactionDecimalNumbersForbidden);
  }
  if (errors.invalidDate) {
    return t(RESOURCES.InvalidDate);
  }
  if (errors.transactionInvalidIBAN) {
    return t(RESOURCES.TransactionInvalidIBAN);
  }
  if (errors.instantPaymentsNotAvailable) {
    return t(RESOURCES.InstantPaymentsNotAvailable);
  }
  if (errors.transactionNotSignable) {
    return t(RESOURCES.TransactionNotSignable);
  }
  if (errors.debtorCreditorAccountIdentical) {
    return t(RESOURCES.DebtorCreditorAccountIdentical);
  }
  if (errors.unspecifiedError) {
    return t(RESOURCES.UnspecifiedError);
  }
  if (errors.transactionAlreadyCompleted) {
    return t(RESOURCES.TransactionAlreadyCompleted);
  }
  if (errors.transactionCancelled) {
    return t(RESOURCES.TransactionCancelled);
  }
  if (errors.transactionExpired) {
    return t(RESOURCES.TransactionExpired);
  }

  // Paypoint active transaction conflict
  if (errors.PayPointPublicID) {
    return t(RESOURCES.ActiveTransactionExists);
  }

  // Bank access token could not be obtained (ZS-1658). An outage on the bank side is a retry, not a re-link;
  // only a rejected token means the connection has to be renewed.
  const accessTokenErrorCode = errors.accessToken?.[0]?.code;
  if (accessTokenErrorCode === ErrorCode.ExternalServiceUnavailable) {
    return t(RESOURCES.BankTemporarilyUnavailable);
  }
  if (accessTokenErrorCode === ErrorCode.Forbidden) {
    return t(RESOURCES.BankConnectionExpired);
  }

  if (status === 403) {
    return t(RESOURCES.TransactionProcessingFailed);
  }

  //... add more errors keys if necessary

  return t(RESOURCES.UnknownError);
};

export const getIsPaypointTransactionConflict = (error: ErrorType) => {
  if (error.data && typeof error.data === "object" && "errors" in error.data) {
    const errorData = error.data as {
      errors: Record<string, unknown>;
    };
    if (errorData.errors?.PayPointPublicID) {
      return true;
    }
  }
  return false;
};
